import { FileIcon } from "@/components/utils/icons/FileIcon";
import { Colors } from "@/enums/Colors";
import { DataRoomFileCategory } from "@/pages/OneDataRoomPage";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Box, Collapse, Typography, styled } from "@mui/material";
import React, { useState } from "react";
import RedFlagSVG from "../../../assets/images/flag.svg";
import DataRoomFileListIcon from "./DataRoomFileListIcon";
import { DataRoomRecursiveList } from "./DataRoomRecursiveList";

type Props = {
  category: DataRoomFileCategory;
  redFlagsCount?: number;
  filesCount?: number;
};

const DataRoomFileListCard = React.memo(function ({ category, redFlagsCount, filesCount }: Props) {
  const [expanded, setExpanded] = useState(false);

  return (
    <CardRoot onClick={() => setExpanded((prev) => !prev)}>
      <CardHeader>
        <Box display="flex" alignItems="center" gap="0.75rem" minWidth={0}>
          <DataRoomFileListIcon img={category.icon} key={category.key + "-icon"} />
          <Typography className="label">{category.label}</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap="0.5rem" sx={{ minWidth: "fit-content" }}>
          {!!filesCount && (
            <Box display="flex" alignItems="center" gap="0.25rem" sx={{ minWidth: "fit-content" }}>
              <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", minWidth: "1rem" }}>{filesCount}</Typography>
              <FileIcon />
            </Box>
          )}
          {!!redFlagsCount && (
            <Box display="flex" alignItems="center" gap="0.25rem" sx={{ minWidth: "fit-content" }}>
              <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", minWidth: "1rem" }}>{redFlagsCount}</Typography>
              <img src={RedFlagSVG.src} alt="redflag-img" style={{ width: "16px", height: "16px" }} />
            </Box>
          )}
          {!filesCount && <Typography className="empty-label">No documents</Typography>}
          {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </Box>
      </CardHeader>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ padding: "0 1rem 1rem 1rem" }} onClick={(e) => e.stopPropagation()}>
          <DataRoomRecursiveList showGo entry={category.entry} level={0} />
        </Box>
      </Collapse>
    </CardRoot>
  );
});

const CardRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  backgroundColor: Colors.WHITE,
  boxShadow: "0 0 20.9px 0 rgba(0, 0, 0, 0.1)",
  borderRadius: "1rem",
  cursor: "pointer",
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 0 28px 0 rgba(0, 0, 0, 0.15)",
  },
}));

const CardHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "1rem",
  gap: "0.5rem",
  [theme.breakpoints.up("sm")]: { padding: "1.25rem" },
  [theme.breakpoints.up("md")]: { padding: "1.5rem" },
  "& .label": {
    fontSize: "0.875rem",
    fontWeight: 600,
    lineHeight: "20px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "& .empty-label": {
    fontSize: "0.75rem",
    fontWeight: 400,
    color: Colors.LIGHT_GRAY_8A8E93,
    fontStyle: "italic",
  },
}));

export default DataRoomFileListCard;
